"use client";

import { RiCloseLine } from "@remixicon/react";
import { Button } from "@workspace/ui/components/button";
import Link from "next/link";
import { useEffect, useState } from "react";
import {
	checkIfCCPAUser,
	checkIfEUUser,
	useConsentStore,
} from "@/lib/analytics/consent";

export function ConsentBanner() {
	const [isVisible, setIsVisible] = useState(false);
	const { shouldShowBanner, acceptAll, rejectAll, setIsEUUser, setIsCCPAUser } =
		useConsentStore();

	useEffect(() => {
		// Check user location on mount
		const checkLocation = async () => {
			const [isEU, isCCPA] = await Promise.all([
				checkIfEUUser(),
				checkIfCCPAUser(),
			]);

			setIsEUUser(isEU);
			setIsCCPAUser(isCCPA);

			// Show banner after a short delay for better UX
			setTimeout(() => {
				setIsVisible(shouldShowBanner());
			}, 1000);
		};

		checkLocation();
	}, [setIsEUUser, setIsCCPAUser, shouldShowBanner]);

	// Re-check visibility when consent state changes
	useEffect(() => {
		setIsVisible(shouldShowBanner());
	}, [shouldShowBanner]);

	const handleAcceptAll = () => {
		acceptAll();
		setIsVisible(false);
	};

	const handleRejectAll = () => {
		rejectAll();
		setIsVisible(false);
	};

	if (!isVisible) return null;

	return (
		<div className="fixed inset-x-0 bottom-0 z-50 p-4 sm:p-6">
			<div className="mx-auto max-w-4xl">
				<div className="relative rounded-lg border bg-background p-6 shadow-lg">
					<button
						type="button"
						onClick={handleRejectAll}
						className="absolute top-2 right-2 rounded-full p-1 hover:bg-muted"
						aria-label="Close"
					>
						<RiCloseLine className="h-5 w-5" />
					</button>

					<div className="mb-4 pr-8">
						<h3 className="font-semibold text-lg">We value your privacy</h3>
						<p className="mt-2 text-muted-foreground text-sm">
							We use cookies and similar technologies to improve your experience
							on Humble. This includes basic analytics, growth analytics to
							understand user behavior, and optional features like session
							recording and A/B testing. By clicking "Accept All", you consent
							to all analytics features. You can manage your preferences or
							learn more in our{" "}
							<Link
								href="/legal/privacy"
								className="underline hover:text-primary"
							>
								Privacy Policy
							</Link>
							.
						</p>
					</div>

					<div className="flex flex-col gap-3 sm:flex-row sm:justify-end">
						<Button variant="outline" asChild className="w-full sm:w-auto">
							<Link href="/settings/privacy">Manage Preferences</Link>
						</Button>
						<Button
							variant="outline"
							onClick={handleRejectAll}
							className="w-full sm:w-auto"
						>
							Reject All
						</Button>
						<Button onClick={handleAcceptAll} className="w-full sm:w-auto">
							Accept All
						</Button>
					</div>
				</div>
			</div>
		</div>
	);
}
